import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../hooks/use-is-mounted";
import {DialogActions, DialogContent, DialogTitle, Divider} from "@mui/material";
import Button from "../../base/button/button";
import Dialog from "../../base/dialog";
import {Col} from "reactstrap";
import Chips, {ChipsTypes} from "../../base/chips/chips";
import Api from "../../../../core/services/api/api";
import {OutlinedDropdown} from "../../base/dropdown/outlined_dropdown";
import {OutlinedInput} from "../../base/input/outlined_input";
import {ReactComponent as EnterIcon} from "../../../../assets/images/products/enter.svg";
import AutocompleteBase from "../../text-fields/autocomplete/autocomplete-base";


const SelectProductSerialNumbersDialog = ({open, close, onFinish, partNo, freeSolo = false}) => {
    const [data, setData] = useState({});
    const [selectedWarehouse, setSelectedWarehouse] = useState(null);
    const [availableWarehouses, setAvailableWarehouses] = useState([]);
    const [availableSerialNumbers, setAvailableSerialNumbers] = useState([]);
    const [freeSoloValue, setFreeSoloValue] = useState('');
    const isMounted = useIsMounted();

    const warehouseOptions = useMemo(() =>
        availableWarehouses.map(e => ({
            id: e.id,
            value: e,
            label: e.name ?? '',
        })) ?? [], [availableWarehouses]);

    const serialNumbersOptions = useMemo(() =>
        availableSerialNumbers
            .filter(e => !Object.values(data).some(warehouseSerialNumbers => warehouseSerialNumbers.includes(e)))
            .map(e => ({
                id: e.serialNumber,
                value: e,
                label: e.serialNumber,
            })) ?? [], [availableSerialNumbers, data]);

    const noSerialsSelected = useMemo(() => !Object.keys(data).some(e => data[e].length !== 0), [data]);

    const renderedData = useMemo(() =>
        Object
            .keys(data)
            .filter(warehouseCode => data[warehouseCode]?.length > 0)
            .map(warehouseCode => ({
                warehouseTitle: availableWarehouses?.find(e => e.code === warehouseCode)?.name ?? (freeSolo ? 'Manually Entered' : ""),
                warehouseCode: warehouseCode,
                serialNumbers: data[warehouseCode] ?? [],
            })), [availableWarehouses, data, freeSolo])

    /**
     * Fetches the operator's warehouses from the server.
     * @type {(function(): Promise<void>)}
     */
    const getWarehouses = useCallback(async () => {
        const response = await Api.getOperatorWarehouses();
        if (!isMounted())
            return;
        const list = response?.data ?? [];
        setAvailableWarehouses(list);
        if (list) {
            setSelectedWarehouse(list[0]);
        }
    }, [isMounted]);

    /**
     * With each change in open state of the dialog:
     * - If the dialog is opened, fetch the operator's warehouses.
     */
    useEffect(() => {
        if (!open)
            return setAvailableWarehouses([]);
        getWarehouses().then();
    }, [getWarehouses, open])

    /**
     * Fetches the serial numbers for the selected warehouse from the server.
     * @type {(function(): Promise<void>)|*}
     */
    const getSerialNumbers = useCallback(async () => {
        if (!selectedWarehouse?.code)
            return setAvailableSerialNumbers([]);
        const forApi = {
            partNo: partNo,
            warehouseCode: selectedWarehouse?.code,
        }
        const response = await Api.getPartSerialNosAndQuantityByWarehouse(forApi)
        if (!isMounted())
            return;
        const list = response?.data?.serials?.map(serialNo => ({
            serialNumber: serialNo,
            warehouseCode: selectedWarehouse?.code,
        })) ?? [];
        setAvailableSerialNumbers(list);
    }, [isMounted, partNo, selectedWarehouse?.code]);

    /**
     * With each change in open state of the dialog:
     * - If the dialog is opened, fetch the serial numbers for the selected warehouse.
     * - If the dialog is closed, reset the list of serial numbers.
     */
    useEffect(() => {
        if (!open)
            return setAvailableSerialNumbers([]);
        getSerialNumbers().then();
    }, [open, getSerialNumbers])

    /**
     * Handles selection of a serial number from the list of serial numbers
     * @type {(function(*): void)}
     */
    const addSerialNumber = useCallback((serialNumber) => {
        if (!selectedWarehouse)
            return;
        setData(prevState => {
            // if serial number exists, do nothing:
            if (prevState?.[selectedWarehouse.code]?.includes(serialNumber))
                return prevState;
            return ({
                ...prevState,
                [selectedWarehouse.code]: [
                    ...(prevState?.[selectedWarehouse.code] ?? []),
                    serialNumber,
                ],
            })
        });
    }, [selectedWarehouse]);

    /**
     * Handles removal of a serial number from the list of serial numbers
     * @type {(function(*, *): void)}
     */
    const removeSerialNumber = useCallback((serialNumber, warehouseCode) => {
        setData(prevState => ({
            ...prevState,
            [warehouseCode]: prevState?.[warehouseCode]?.filter(e => e.serialNumber !== serialNumber),
        }));
    }, []);

    /**
     * Finishes the process of selecting serial numbers and closes the dialog.
     * @type {(function(): void)|*}
     */
    const finishProcess = useCallback(() => {
        const serialNumbers = Array(...new Set(Object
            .keys(data)
            .reduce((acc, warehouseCode) => [...acc, ...(data[warehouseCode]?.map(e => e.serialNumber) ?? [])], [])
        ).values());
        onFinish(serialNumbers);
        close();
    }, [data, onFinish, close]);


    /**
     * With each change in open state of the dialog:
     * - If the dialog is closed, reset the data.
     */
    useEffect(() => {
        if (!open)
            setData({});
    }, [open])


    /**
     * Submits the free solo value to the list of serial numbers
     * @type {(function(): void)}
     */
    const submitFreeSolo = useCallback(() => {
        const freeSoloWarehouseCode = '__free_solo__';
        let _freeSoloValue = freeSoloValue.trim()
        if (!!_freeSoloValue) {
            setData(prevState => {
                if (prevState?.[freeSoloWarehouseCode]?.includes(_freeSoloValue))
                    return prevState;
                return ({
                    ...prevState,
                    [freeSoloWarehouseCode]: [
                        ...(prevState?.[freeSoloWarehouseCode] ?? []),
                        {serialNumber: _freeSoloValue, warehouseCode: freeSoloWarehouseCode},
                    ],
                })
            });
        }
        setFreeSoloValue('');
    }, [freeSoloValue])

    return (
        <>
            <Dialog
                open={open}
                setOpen={close}
                maxWidth={'md'}
                className={'bound-dialog select-product-serial-numbers-dialog'}
            >
                <DialogTitle className={'p-0'} id="scroll-dialog-title">
                    <p className={'text-lg text-color-1 p-4 font-weight-semi-bold text-center'}>
                        Select Serial Numbers for {partNo ?? ''}
                    </p>
                </DialogTitle>
                <DialogContent dividers className={'py-3'}>
                    <div className={'d-flex flex-wrap w-100'}>
                        <Col xs={12} md={6} className={'mb-4'}>
                            <OutlinedDropdown
                                label={'Warehouse'}
                                value={selectedWarehouse ?? ''}
                                onChange={setSelectedWarehouse}
                                data={warehouseOptions}
                            />
                        </Col>
                        <Col xs={12} md={6} className={'mb-4'}>
                            <AutocompleteBase
                                label={'Serial Number'}
                                value={null}
                                clearOnEscape
                                disableCloseOnSelect
                                onChange={(_, v) => v && addSerialNumber(v.value)}
                                getOptionLabel={v => v?.label ?? ''}
                                getOptionKey={v => v?.id ?? ''}
                                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                options={serialNumbersOptions}
                                filterOptions={e => e}
                            />
                        </Col>
                        {
                            freeSolo &&
                            <Col xs={12} className={'mb-4'}>
                                <Divider/>
                                <OutlinedInput
                                    className={'mt-3'}
                                    onKeyUp={(e) => e.key === "Enter" && submitFreeSolo()}
                                    type={'text'}
                                    label={"Free Solo"}
                                    placeholder={'Enter your custom serial number here'}
                                    icon={<EnterIcon className={'enter-icon'}/>}
                                    value={freeSoloValue}
                                    onChange={setFreeSoloValue}
                                    onIconClick={(e) => submitFreeSolo()}
                                />
                            </Col>
                        }
                        <Col xs={12} className={'mb-4'}>
                            <div className={'entities-container'}>
                                {
                                    renderedData.map(({warehouseTitle, warehouseCode, serialNumbers}) => (
                                        <React.Fragment key={warehouseCode}>
                                            <p className={'entity-title'}>
                                                {warehouseTitle}:
                                            </p>
                                            <div className={'d-flex flex-wrap'}>
                                                {
                                                    serialNumbers.map(serialNumber => (
                                                        <Chips
                                                            key={serialNumber.serialNumber}
                                                            className={'mr-2 mb-2'}
                                                            type={ChipsTypes.remove}
                                                            content={serialNumber.serialNumber}
                                                            onIconClick={() => removeSerialNumber(serialNumber.serialNumber, serialNumber.warehouseCode)}
                                                        />
                                                    ))
                                                }
                                            </div>
                                        </React.Fragment>
                                    ))
                                }
                            </div>
                        </Col>
                    </div>
                </DialogContent>
                <DialogActions className={'p-0 w-100'}>
                    <div className={'d-flex justify-content-end w-100'}>
                        <Button
                            disabled={!open || noSerialsSelected}
                            variant={'dialog'}
                            color={'primary'}
                            type={'submit'}
                            onClick={finishProcess}
                            borderRadius={'8px 0 8px 0'}
                        >
                            Finish Process
                        </Button>
                    </div>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default SelectProductSerialNumbersDialog;
